import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import get from 'lodash.get';
import compact from 'lodash.compact';
import uniq from 'lodash.uniq';
import {
    initialState as pdfInitialState,
    reducer as pdfReducer,
} from '../../api/pdfGeneration/shared/pdfReducer';
import createSelectors from '../../utils/shared/selectorUtils';
import { getStudentsByGrade } from '../../utils/shared/filterUtils';
import {
    initialState as dualLanguageInitialState,
    reducer as dualLanguageReducer,
} from '../../api/dualLanguage/dualLanguageReducer';
import {
    initialState as demoModeInitialState,
    reducer as demoModeReducer,
    selectors as demoModeSelectors,
} from '../../api/demoMode/reading/demoModeReducer';
import {
    selectors as formsSelectors,
    initialState as formsInitialState,
} from '../../api/forms/shared/formsReducer';
import {
    initialState as staffInitialState,
    reducer as staffReducer,
    selectors as staffSelectors,
} from '../../api/staff/shared/staffReducer';
import {
    initialState as applicationConfigInitialState,
    reducer as applicationConfigReducer,
    selectors as applicationConfigSelectors,
} from '../../api/applicationConfig/shared/applicationConfigReducer';
import {
    initialState as studentSummaryInitialState,
    reducer as studentSummaryReducer,
} from '../../api/studentSummary/shared/studentSummaryReducer';
import {
    initialState as percentilesInitialState,
    reducer as percentilesReducer,
    selectors as percentilesSelectors,
} from '../../api/percentiles/percentilesReducer';
import {
    initialState as lexileInitialState,
    reducer as lexileReducer,
    selectors as lexileSelectors,
} from '../../api/lexile/lexileReducer';
import {
    initialState as intercomInitialState,
    reducer as intercomReducer,
    selectors as intercomSelectors,
} from '../../api/intercom/intercomReducer';
import {
    initialState as errorInitialState,
    reducer as errorReducer,
    selectors as errorSelectors,
} from '../../api/error/errorReducer';
import {
    initialState as enrollmentInitialState,
    reducer as enrollmentReducer,
} from '../../api/enrollment/reading/enrollmentReducer';
import {
    initialState as resultInitialState,
    reducer as resultReducer,
} from '../../api/results/reading/resultReducer';
import formsReducer from '../../api/forms/reading/formsReducer';
import {
    initialState as interpInitialState,
    reducer as interpReducer,
} from '../../api/interps/reading/interpReducer';
import {
    initialState as interventionsInitialState,
    reducer as interventionsReducer,
    selectors as interventionsSelectors,
} from '../../api/interventions/interventionsReducer';
import {
    initialState as userInitialState,
    reducer as tokenReducer,
} from '../../api/keycloak/tokenReducer';
import {
    initialState as mazeWebEntryInitialState,
    reducer as mazeWebEntryReducer,
    selectors as mazeWebEntrySelectors,
} from '../../api/basicComprehensionWebEntry/shared/basicComprehensionWebEntryReducer';
import {
    initialState as featureReportInitialState,
    reducer as featureReportReducer,
    selectors as featureReportSelectors,
} from '../../api/featureReport/featureReportReducer';
import {
    initialState as troBooksDataInitialState,
    reducer as troBooksDataReducer,
    selectors as troBooksDataSelectors,
} from '../../api/troBooksData/troBooksDataReducer';
import {
    initialState as instructionInitialState,
    reducer as instructionReducer,
} from '../../api/instruction/reading/instructionReducer';
import {
    initialState as studentGoalsInitialState,
    reducer as studentGoalsReducer,
    selectors as studentGoalsSelectors,
} from '../../api/studentGoals/shared/studentGoalsReducer';

import {
    initialState as modalsState,
    reducer as modalsReducer,
    selectors as modalsSelectors,
} from '../../api/modals/reading/modalsReducer';
import { D8_MEASURES } from '../../constants/shared/MeasuresConstants';
import { LICENSE } from '../../constants/reading/CommonConstants';
import { LANGUAGE, PERIOD, NAME } from '../../constants/shared/CommonConstants';
import {
    MEASURE,
    MEASURES_KEYS,
    HOME_CONNECT_AVAILABLE_MEASURES,
    HOME_CONNECT_LETTER_MEASURES,
    HOME_CONNECT_MEASURE_TITLES,
    FR_EXPRESS_VIEW_MEASURES,
} from '../../constants/reading/MeasuresConstants';
import { getStudentDataByMeasures } from '../../utils/reading/selectorUtils';
import { getBookById, getCompletedBookResults } from '../../utils/reading/resultReducerHelper';
import { INSTRUCTION_GROUPS_TYPES } from '../../constants/shared/InstructionConstants';
import {
    getGradeToMeasureMapperWithLexile,
    getMeasureMapper,
    getBenchmarkData,
    getStudentsDataByMeasuresForAllPMGraphs,
    getChosenClassStudentsMazeResultsByGrade,
    getTROSentences,
    getReadingStats,
    getInstructionStudentsDataForSelectedGrade,
    getStudentComputedGoals,
    getStudentsCompositeGoals,
    getPerformanceTables,
    getResultsTableMeasures,
    getResultsTableSummaryMeasures,
    getStudentSummaryTableMeasures,
    getBenchmarkHistoryData,
    areTogglesEnabled,
    getIntercomInfo,
    getInstructionGroupsForSelectedGrade,
    getStudentsPmDataByGrades,
    getSummaryInfoData,
    getNumberOfAssessedStudentsExpress,
    getNumberOfAssessedStudents,
    getNumberOfStudentsInProgress,
    getStatusesByYearPeriodGradeMeasure,
    getTroClassSummaryPdfData,
    getAvailablePrintItems,
    getClassSummaryPdfData,
} from './rootHelpers';
import {
    getHomeConnectStudentsData,
    getAllStudentsInClassData,
    getDualLanguageStudentData,
    getHomeConnectPDFStudentsData,
    getStaffIdsFromStudentsBasicComprehensionResults,
    getDualLanguageClassSummaryPdfData,
    getHistoricalGradeToMeasuresMapper,
    getStudentsGrowthAnalysisDataByGrades,
    getStudentsZoGDataByGrades,
} from '../shared/rootHelpers';
import sharedSelectors from '../shared/root';
import { getInstructionPeriod } from '../../utils/shared/instructionUtils';
import { getGoalReading } from '../../utils/reading/cellUtils';
import {
    ALL_MEASURES,
    getExtendableMeasuresAvailabilityInGrade,
} from '../../constants/reading/HistoricalMeasuresAvailabilityByGrades';
import isStandaloneExpress from '../../utils/reading/expressUtils';
import {
    filterOutAdditionalMeasures,
    filterAdditionalMeasuresFromMapper,
    filterResultsOfAdditionalMeasures,
} from '../../utils/shared/additionalMeasureUtils';
import { getD8MeasureByGrade } from '../../constants/reading/DefaultGradeToMeasureMapper';
import { GRADE } from '../../constants/shared/GradesConstants';

const { ENGLISH, SPANISH } = LANGUAGE;
const { BOY_MOY, MOY_EOY } = PERIOD;
const { OVERALL, DO, TRC, TRO } = MEASURE;

const appReducers = {
    staff: staffReducer,
    applicationConfig: applicationConfigReducer,
    error: errorReducer,
    enrollment: enrollmentReducer,
    userInfo: tokenReducer,
    results: resultReducer,
    interps: interpReducer,
    interventions: interventionsReducer,
    featureReport: featureReportReducer,
    forms: formsReducer,
    studentSummary: studentSummaryReducer,
    troBooksData: troBooksDataReducer,
    instruction: instructionReducer,
    dualLanguage: dualLanguageReducer,
    basicComprehensionWebEntry: mazeWebEntryReducer,
    pdf: pdfReducer,
    studentGoals: studentGoalsReducer,
    percentiles: percentilesReducer,
    intercomInfo: intercomReducer,
    lexile: lexileReducer,
    demoMode: demoModeReducer,
    modals: modalsReducer,
};

export const initialState = {
    staff: staffInitialState,
    applicationConfig: applicationConfigInitialState,
    error: errorInitialState,
    enrollment: enrollmentInitialState,
    userInfo: userInitialState,
    results: resultInitialState,
    interps: interpInitialState,
    interventions: interventionsInitialState,
    featureReport: featureReportInitialState,
    forms: formsInitialState,
    studentSummary: studentSummaryInitialState,
    troBooksData: troBooksDataInitialState,
    instruction: instructionInitialState,
    dualLanguage: dualLanguageInitialState,
    basicComprehensionWebEntry: mazeWebEntryInitialState,
    pdf: pdfInitialState,
    studentGoals: studentGoalsInitialState,
    percentiles: percentilesInitialState,
    intercomInfo: intercomInitialState,
    lexile: lexileInitialState,
    demoMode: demoModeInitialState,
    modals: modalsState,
};

export const rootReducer = reducers => combineReducers({ ...appReducers, ...reducers });

export const selectors = {
    ...createSelectors(staffSelectors, 'staff'),
    ...createSelectors(applicationConfigSelectors, 'applicationConfig'),
    ...createSelectors(formsSelectors, 'forms'),
    ...createSelectors(errorSelectors, 'error'),
    ...createSelectors(featureReportSelectors, 'featureReport'),
    ...createSelectors(troBooksDataSelectors, 'troBooksData'),
    ...createSelectors(mazeWebEntrySelectors, 'basicComprehensionWebEntry'),
    ...createSelectors(interventionsSelectors, 'interventions'),
    ...createSelectors(studentGoalsSelectors, 'studentGoals'),
    ...createSelectors(percentilesSelectors, 'percentiles'),
    ...createSelectors(intercomSelectors, 'intercomInfo'),
    ...createSelectors(lexileSelectors, 'lexile'),
    ...createSelectors(demoModeSelectors, 'demoMode'),
    ...createSelectors(modalsSelectors, 'modals'),
    ...sharedSelectors,
};

selectors.isDataReady = createSelector(
    [selectors.isDataReady, selectors.isStudentGoalsFetched],
    (isDataReady, isStudentsGoalsFetched) => isDataReady && isStudentsGoalsFetched,
);

selectors.getEnrollmentInfo = createSelector(
    [selectors.getEnrollmentInfo, selectors.getBurstGroupId],
    (enrollmentInfo, burstGroupUid) => ({
        ...enrollmentInfo,
        burstGroupUid,
    }),
);

selectors.getChosenClassStudentsMazeResultsByGrade = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getSelectedPeriod,
        selectors.getSelectedPmPeriod,
        selectors.getWindowDates,
        selectors.getToyPeriods,
    ],
    ({ studentsByGrade, results, year }, period, pmPeriod, windowDates, toyPeriods) =>
        getChosenClassStudentsMazeResultsByGrade(
            studentsByGrade,
            results,
            period,
            year,
            pmPeriod,
            windowDates,
            toyPeriods,
        ),
);

selectors.getHistoricalGradeToMeasuresMapper = createSelector(
    [selectors.getGradeToMeasureMapper],
    gradeToMeasureMapper =>
        getHistoricalGradeToMeasuresMapper(
            gradeToMeasureMapper,
            getExtendableMeasuresAvailabilityInGrade,
            ALL_MEASURES,
        ),
);

selectors.getStudentComputedGoals = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getInterps,
        selectors.getStudentGoals,
        selectors.getHistoricalGradeToMeasuresMapper,
        selectors.getToyPeriods,
    ],
    ({ studentsByGrade, results, year }, interps, customGoals, gradeToMeasureMapper, toyPeriods) =>
        getStudentComputedGoals(
            studentsByGrade,
            results,
            interps,
            customGoals,
            year,
            filterAdditionalMeasuresFromMapper(gradeToMeasureMapper),
            toyPeriods,
        ),
);

selectors.getStudentsPmDataByGrades = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getInterps,
        selectors.getSelectedPmPeriod,
        selectors.getWindowDates,
        selectors.getStudentComputedGoals,
        selectors.getToyPeriods,
    ],
    ({ studentsByGrade, results, year }, interps, pmPeriod, windowDates, goals, toyPeriods) =>
        getStudentsPmDataByGrades(
            studentsByGrade,
            results,
            interps,
            year,
            pmPeriod,
            windowDates,
            goals,
            toyPeriods,
        ),
);

selectors.getStudentsGrowthAnalysisDataByGrades = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getInterps,
        selectors.getStudentComputedGoals,
        selectors.getGrowthAnalysisPeriodObject,
        selectors.getGradeToMeasureMapper,
    ],
    (
        { studentsByGrade, results, year },
        interps,
        goals,
        { startPeriod, endPeriod },
        gradeToMeasureMapper,
    ) =>
        getStudentsGrowthAnalysisDataByGrades(
            studentsByGrade,
            results,
            interps,
            goals,
            year,
            startPeriod,
            endPeriod,
            filterAdditionalMeasuresFromMapper(gradeToMeasureMapper),
            getD8MeasureByGrade,
        ),
);

selectors.getSelectedPmPeriodForGoal = createSelector(
    [selectors.getSelectedPmPeriod],
    selectedPmPeriod => (selectedPmPeriod === BOY_MOY ? BOY_MOY : MOY_EOY),
);

selectors.getStudentsCompositeGoals = createSelector(
    [
        selectors.getChosenClassStudentsByGrade,
        selectors.getStudentComputedGoals,
        selectors.getSelectedPmPeriodForGoal,
    ],
    (studentsByGrade, studentsGoals, period) =>
        getStudentsCompositeGoals(studentsByGrade, studentsGoals, period),
);

selectors.getStudentsZoGDataByGrades = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getInterps,
        selectors.getStudentComputedGoals,
        selectors.getStudentsCompositeGoals,
        selectors.getSelectedPmPeriodForGoal,
        selectors.getGradeToMeasureMapper,
    ],
    (
        { studentsByGrade, results, year },
        interps,
        goals,
        compositeGoals,
        pmPeriod,
        gradeToMeasureMapper,
    ) =>
        getStudentsZoGDataByGrades(
            studentsByGrade,
            results,
            interps,
            goals,
            compositeGoals,
            year,
            pmPeriod,
            filterAdditionalMeasuresFromMapper(gradeToMeasureMapper),
            getD8MeasureByGrade,
        ),
);

selectors.areTogglesEnabled = createSelector(
    [selectors.getResults, selectors.getSelectedPeriod, selectors.getCurrentSchoolYear],
    (results, selectedPeriod, selectedYear) =>
        areTogglesEnabled(results, selectedPeriod, selectedYear),
);

selectors.getInstructionPeriod = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getCurrentPeriod,
        selectors.getWindowDates,
        selectors.getToyPeriods,
        selectors.getSelectedInstructionGrade,
        selectors.getLicensingInfo,
    ],
    (
        { results, year },
        currentPeriod,
        windowDates,
        toyPeriods,
        selectedInstructionGrade,
        licensingInfo,
    ) =>
        getInstructionPeriod(
            results,
            year,
            currentPeriod,
            [...D8_MEASURES, DO, TRC, TRO],
            windowDates,
            toyPeriods,
            selectedInstructionGrade,
            licensingInfo,
        ),
);

selectors.getCheckPointPeriod = createSelector(
    [
        selectors.getSelectedGradeCheckPointObject,
        selectors.getSelectedInstructionDate,
        selectors.getInstructionPeriod,
    ],
    (checkPointObject, selectedDate, instructionPeriod) =>
        get(checkPointObject.dateToPeriod, `${selectedDate}`, instructionPeriod),
);

selectors.getInstructionStudentsDataForSelectedGrade = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getCheckPointPeriod,
        selectors.getSelectedInstructionGrade,
        selectors.getWindowDates,
        selectors.getSelectedInstructionDate,
        selectors.getLicensingInfo,
        selectors.getCurrentPeriod,
        selectors.getInterventions,
        selectors.getObservations,
        selectors.getToyPeriods,
    ],
    (
        { studentsByGrade, results: studentsResults, year: currentYear },
        checkPointPeriod,
        selectedGrade,
        windowDates,
        selectedDate,
        licensingInfo,
        currentPeriod,
        interventions,
        observationsContent,
        toyPeriods,
    ) =>
        getInstructionStudentsDataForSelectedGrade({
            studentsByGrade,
            studentsResults: filterResultsOfAdditionalMeasures(studentsResults),
            currentYear,
            checkPointPeriod,
            selectedGrade,
            selectedDate,
            windowDates,
            licensingInfo,
            currentPeriod,
            interventions,
            observationsContent,
            toyPeriods,
        }),
);

selectors.getInstructionGroupsForSelectedGrade = createSelector(
    [
        selectors.getSelectedInstructionGrade,
        selectors.getCheckPointPeriod,
        selectors.getInstructionStudentsDataForSelectedGrade,
        selectors.getLicensingInfo,
        selectors.getObservations,
    ],
    (selectedGrade, checkPointPeriod, studentsData, licensingInfo, observations) =>
        getInstructionGroupsForSelectedGrade(
            selectedGrade,
            checkPointPeriod,
            studentsData,
            licensingInfo,
            observations,
        ),
);

selectors.getStatusesByYearPeriodGradeMeasure = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getSelectedPeriod,
        selectors.getSelectedGrade,
        selectors.getLicensingInfo,
    ],
    ({ results, year }, period, grade, licenseInfo) =>
        getStatusesByYearPeriodGradeMeasure(results, year, period, grade, licenseInfo),
);

selectors.getPerformanceTables = createSelector(
    [
        selectors.getCurrentPeriod,
        selectors.getSelectedPeriod,
        selectors.getSelectedGradesForClassPerformance,
        selectors.getStatusesByYearPeriodGradeMeasure,
        selectors.getLicensingInfo,
        selectors.getGradeToMeasureMapper,
        selectors.getToyPeriods,
    ],
    (
        currentPeriod,
        selectedPeriod,
        selectedGrades,
        statuses,
        licenseInfo,
        gradeToMeasureMapper,
        toyPeriods,
    ) =>
        getPerformanceTables(
            currentPeriod,
            selectedPeriod,
            selectedGrades,
            filterOutAdditionalMeasures(statuses),
            licenseInfo,
            filterAdditionalMeasuresFromMapper(gradeToMeasureMapper),
            toyPeriods,
        ),
);

selectors.getStaffIdsFromStudentsMazeResults = createSelector(
    [selectors.getChosenClassStudentsMazeResultsByGrade],
    studentsByGrade => getStaffIdsFromStudentsBasicComprehensionResults(studentsByGrade),
);

selectors.getUpdatedGradeSpecificMeasureMapper = createSelector(
    [
        selectors.getGradeToMeasureMapper,
        selectors.isLexileShown,
        selectors.getLicensingInfo,
        selectors.areTogglesEnabled,
    ],
    (gradeToMeasureMapper, isLexileShown, licensingInfo, { lexile }) =>
        licensingInfo.includes(LICENSE.D8) && lexile && isLexileShown
            ? getGradeToMeasureMapperWithLexile(gradeToMeasureMapper)
            : gradeToMeasureMapper,
);

selectors.getResultsTableMeasures = createSelector(
    [
        selectors.getSelectedPeriod,
        selectors.getGradesInCurrentClass,
        selectors.getLicensingInfo,
        selectors.getUpdatedGradeSpecificMeasureMapper,
        selectors.getToyPeriods,
    ],
    (selectedPeriod, gradesInCurrentClass, licensingInfo, gradeToMeasureMapper, toyPeriods) =>
        getResultsTableMeasures(
            selectedPeriod,
            gradesInCurrentClass,
            licensingInfo,
            gradeToMeasureMapper,
            toyPeriods,
        ),
);

selectors.getResultsTableSummaryMeasures = createSelector(
    [
        selectors.getGradesInCurrentClass,
        selectors.getLicensingInfo,
        selectors.getUpdatedGradeSpecificMeasureMapper,
    ],
    (gradesInCurrentClass, licensingInfo, gradeToMeasureMapper) =>
        getResultsTableSummaryMeasures(gradesInCurrentClass, licensingInfo, gradeToMeasureMapper),
);

selectors.getAllAvailableMeasuresForGrades = createSelector(
    [selectors.getHistoricalGradeToMeasuresMapper],
    historicalGradeToMeasuresMapper => {
        return uniq(
            historicalGradeToMeasuresMapper.reduce((acc, { measures }) => {
                acc.push(...measures);

                return acc;
            }, []),
        );
    },
);

selectors.getStudentSummaryTableMeasures = createSelector(
    [selectors.getGradeYears, selectors.getHistoricalGradeToMeasuresMapper],
    (gradeYears, historicalGradeToMeasuresMapper) =>
        getStudentSummaryTableMeasures(gradeYears, historicalGradeToMeasuresMapper),
);

selectors.getInstructionStudentsColumns = createSelector(
    [selectors.getLicensingInfo],
    licensingInfo => [
        NAME,
        ...(isStandaloneExpress(licensingInfo)
            ? FR_EXPRESS_VIEW_MEASURES
            : compact([OVERALL, licensingInfo.includes(TRC) ? TRC : null])),
    ],
);

selectors.getDataByMeasuresForStudentProgress = createSelector(
    [
        selectors.getStudentStatusesForSelectedStudent,
        selectors.getInterps,
        selectors.getSelectedGradeYear,
        selectors.getLicensingInfo,
        selectors.getStudentComputedGoals,
        selectors.getSelectedStudentId,
        selectors.getHistoricalGradeToMeasuresMapper,
    ],
    (
        studentStatuses,
        interps,
        selectedGradeYear,
        licensingInfo,
        studentGoals,
        selectedStudentId,
        historicalGradeToMeasuresMapper,
    ) =>
        getStudentDataByMeasures(
            filterOutAdditionalMeasures(studentStatuses, false),
            interps,
            selectedGradeYear.grade,
            selectedGradeYear.year,
            licensingInfo,
            studentGoals,
            selectedStudentId,
            filterAdditionalMeasuresFromMapper(historicalGradeToMeasuresMapper),
        ),
);

selectors.getHomeConnectStudentsData = createSelector(
    [
        selectors.getSelectedPeriod,
        selectors.getCurrentPeriod,
        selectors.getAllStudentsStatuses,
        selectors.getGradeToMeasureMapper,
    ],
    (selectedPeriod, currentPeriod, allStudentsStatuses, gradeAvailability) =>
        getHomeConnectStudentsData(
            selectedPeriod,
            currentPeriod,
            allStudentsStatuses,
            gradeAvailability,
            D8_MEASURES,
            HOME_CONNECT_MEASURE_TITLES,
        ),
);

selectors.getHomeConnectPDFStudentsData = createSelector(
    [
        selectors.getSelectedPeriod,
        selectors.getCurrentPeriod,
        selectors.getInterps,
        selectors.getAllStudentsStatuses,
        selectors.getGradeToMeasureMapper,
    ],
    (selectedPeriod, currentPeriod, interps, allStudentsStatuses, gradeToMeasureMapper) =>
        getHomeConnectPDFStudentsData(
            selectedPeriod,
            currentPeriod,
            interps,
            allStudentsStatuses,
            INSTRUCTION_GROUPS_TYPES.READING,
            HOME_CONNECT_AVAILABLE_MEASURES,
            HOME_CONNECT_LETTER_MEASURES,
            gradeToMeasureMapper,
            getMeasureMapper,
        ),
);

selectors.getDualLanguageStudentsData = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getSelectedPeriodWithoutSummary,
        selectors.getSelectedDualLanguageGrade,
        selectors.getDualLanguageGradeAvailabilityInfo,
    ],
    (studentsResults, period, selectedGrade, dualLanguageGradeAvailabilityInfo) => {
        const { studentsByGrade, results, year } = studentsResults;
        const { gradeAvailabilityInfo, additionalGradeAvailabilityInfo } =
            dualLanguageGradeAvailabilityInfo;
        return getStudentsByGrade(studentsByGrade, selectedGrade).map(student =>
            getDualLanguageStudentData(student, results, year, period, selectedGrade, {
                statusesLanguage: ENGLISH,
                additionalStatusesLanguage: SPANISH,
                gradeAvailabilityInfo,
                additionalGradeAvailabilityInfo,
            }),
        );
    },
);

selectors.getDualLanguageClassSummaryPdfData = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getSelectedPeriodWithoutSummary,
        selectors.getDualLanguageGradeAvailabilityInfo,
    ],
    (studentsResults, period, dualLanguageGradeAvailabilityInfo) => {
        const { studentsByGrade, results, year } = studentsResults;
        const { gradeAvailabilityInfo, additionalGradeAvailabilityInfo } =
            dualLanguageGradeAvailabilityInfo;
        return getDualLanguageClassSummaryPdfData(studentsByGrade, results, year, period, {
            statusesLanguage: ENGLISH,
            additionalStatusesLanguage: SPANISH,
            gradeAvailabilityInfo,
            additionalGradeAvailabilityInfo,
        });
    },
);

selectors.isCurrentSchoolYear = createSelector(
    [selectors.getCurrentSchoolYear, selectors.getSelectedGradeYear],
    (currentYear, { year: selectedYear }) => currentYear === selectedYear,
);

selectors.getAllStudentsInClassData = createSelector(
    [
        selectors.getAllStudentSummaryStatuses,
        selectors.getInterps,
        selectors.getCurrentSchoolYear,
        selectors.getHistoricalGradeToMeasuresMapper,
        selectors.getToyPeriods,
        selectors.getSummerPeriodLabel,
        selectors.getLicensingInfo,
    ],
    (
        studentsData,
        interps,
        currentSchoolYear,
        historicalGradeToMeasuresMapper,
        toyPeriods,
        summerPeriodLabel,
        licenseInfo,
    ) =>
        getAllStudentsInClassData(
            [...D8_MEASURES, TRC],
            studentsData,
            interps,
            currentSchoolYear,
            MEASURES_KEYS,
            historicalGradeToMeasuresMapper,
            getGoalReading,
            toyPeriods,
            summerPeriodLabel,
            licenseInfo.includes(LICENSE.LOUISIANA),
        ),
);

selectors.getBenchmarkData = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getInterps,
        selectors.getResultsTableMeasures,
        selectors.getSelectedPeriod,
        selectors.getCurrentPeriod,
        selectors.getUpdatedGradeSpecificMeasureMapper,
        selectors.isLexileShown,
        selectors.getIsDTC,
    ],
    (
        { studentsByGrade, results, year },
        interps,
        tableMeasures,
        selectedPeriod,
        currentPeriod,
        gradeToMeasureMapper,
        isLexileShown,
        isDTC,
    ) =>
        getBenchmarkData({
            studentsByGrade,
            results,
            interps,
            tableMeasures,
            year,
            selectedPeriod,
            currentPeriod,
            gradeToMeasureMapper,
            isLexileShown,
            isDTC,
        }),
);

selectors.getBenchmarkHistoryData = createSelector(
    [
        selectors.getInterps,
        selectors.getGradeYears,
        selectors.getStudentSummaryTableMeasures,
        selectors.getStudentStatusesForSelectedStudent,
        selectors.getHistoricalGradeToMeasuresMapper,
        selectors.getAllAvailableMeasuresForGrades,
        selectors.getToyPeriods,
        selectors.getSummerPeriodLabel,
        selectors.getLicensingInfo,
    ],
    (
        interps,
        gradeYears,
        measures,
        statuses,
        historicalGradeToMeasuresMapper,
        allAvailableMeasuresForGrades,
        toyPeriods,
        summerPeriodLabel,
        licensingInfo,
    ) =>
        getBenchmarkHistoryData(
            interps,
            gradeYears,
            measures,
            statuses,
            historicalGradeToMeasuresMapper,
            allAvailableMeasuresForGrades,
            toyPeriods,
            summerPeriodLabel,
            licensingInfo.includes(LICENSE.LOUISIANA),
        ),
);

selectors.getStudentsDataByMeasuresForAllPMGraphs = createSelector(
    [
        selectors.getAllStudentsStatuses,
        selectors.getInterps,
        selectors.getLicensingInfo,
        selectors.getStudentComputedGoals,
        selectors.getCurrentPeriod,
        selectors.getCurrentSchoolYear,
        selectors.getGradeToMeasureMapper,
        selectors.getSummerPeriodLabel,
    ],
    (
        studentsWithStatuses,
        interps,
        licensingInfo,
        studentGoals,
        currentPeriod,
        currentSchoolYear,
        gradeToMeasureMapper,
        summerPeriodLabel,
    ) =>
        getStudentsDataByMeasuresForAllPMGraphs(
            studentsWithStatuses,
            interps,
            licensingInfo,
            studentGoals,
            currentPeriod,
            currentSchoolYear,
            filterAdditionalMeasuresFromMapper(gradeToMeasureMapper),
            summerPeriodLabel,
        ),
);

selectors.getBookResultById = (_, { result, selectedBookId }) =>
    getBookById(getCompletedBookResults(result.measureResult), selectedBookId);

selectors.getTROSentences = createSelector(
    [selectors.getTroBookDataById, selectors.getBookResultById],
    (currentBookData, currentBookResult) => getTROSentences(currentBookData, currentBookResult),
);

selectors.getReadingStats = createSelector(
    [selectors.getTroBookDataById, selectors.getBookResultById],
    (currentBookData, currentBookResult) => getReadingStats(currentBookData, currentBookResult),
);

selectors.getIntercomInfo = createSelector(
    [
        selectors.getLocation,
        selectors.getUserFirstName,
        selectors.getUserLastName,
        selectors.getIntercomData,
        selectors.isIntercomInfoFetched,
        selectors.getSelectedPeriod,
        selectors.getSelectedPmPeriod,
        selectors.getSelectedGrowthAnalysisPeriod,
        selectors.getSelectedPeriodWithoutSummary,
        (state, views) => views,
    ],
    (
        location,
        firstName,
        lastName,
        intercomData,
        isIntercomInfoFetched,
        selectedPeriod,
        selectedPmPeriod,
        selectedGaPeriod,
        selectedPeriodWithoutSummary,
        views,
    ) =>
        getIntercomInfo({
            location,
            firstName,
            lastName,
            intercomData,
            isIntercomInfoFetched,
            selectedPeriod,
            selectedPmPeriod,
            selectedGaPeriod,
            selectedPeriodWithoutSummary,
            views,
        }),
);

selectors.getNumberOfAssessedStudentsExpress = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getStudentsIdsByGrade,
        selectors.getSelectedPeriod,
        selectors.getCurrentPeriod,
        selectors.getSelectedGrade,
    ],
    ({ results, year }, studentIdsByGrade, selectedPeriod, currentPeriod, grade) =>
        getNumberOfAssessedStudentsExpress(
            results,
            studentIdsByGrade,
            year,
            selectedPeriod,
            currentPeriod,
            grade,
        ),
);

selectors.getNumberOfAssessedStudents = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getStudentsIdsByGrade,
        selectors.getSelectedPeriod,
        selectors.getCurrentPeriod,
        selectors.getSelectedGrade,
        selectors.getLicensingInfo,
    ],
    ({ results, year }, studentIdsByGrade, selectedPeriod, currentPeriod, grade, licenseInfo) =>
        getNumberOfAssessedStudents(
            results,
            studentIdsByGrade,
            year,
            selectedPeriod,
            currentPeriod,
            grade,
            licenseInfo,
        ),
);

selectors.getNumberOfStudentsInProgress = createSelector(
    [
        selectors.getStudentsResultsCurrentYear,
        selectors.getStudentsIdsByGrade,
        selectors.getSelectedPeriod,
        selectors.getCurrentPeriod,
        selectors.getSelectedGrade,
        selectors.getLicensingInfo,
    ],
    ({ results, year }, studentIdsByGrade, selectedPeriod, currentPeriod, grade, licenseInfo) =>
        getNumberOfStudentsInProgress(
            results,
            studentIdsByGrade,
            year,
            selectedPeriod,
            currentPeriod,
            grade,
            licenseInfo,
        ),
);

selectors.getNumberOfNotAssesedStudents = createSelector(
    [
        selectors.getNumberOfAssessedStudents,
        selectors.getNumberOfStudentsInProgress,
        selectors.getTotalNumberOfStudents,
    ],
    (assessedStudents, inProgressStudents, totalStudentsCount) =>
        totalStudentsCount - assessedStudents - inProgressStudents,
);

selectors.getSummaryInfoData = createSelector(
    [
        selectors.getNumberOfAssessedStudents,
        selectors.getNumberOfStudentsInProgress,
        selectors.getNumberOfAssessedStudentsExpress,
        selectors.getLicensingInfo,
        selectors.getNumberOfNotAssesedStudents,
    ],
    (
        assessedStudentsReading,
        inProgressStudents,
        assessedStudentsExpress,
        licenseInfo,
        notAssessedStudents,
    ) =>
        getSummaryInfoData(
            assessedStudentsReading,
            inProgressStudents,
            assessedStudentsExpress,
            licenseInfo,
            notAssessedStudents,
        ),
);

selectors.getTroClassSummaryPdfData = createSelector(
    [
        selectors.isDataReady,
        selectors.getLicensingInfo,
        selectors.getStudentsResultsCurrentYear,
        selectors.getSelectedPeriod,
        selectors.getGradeToMeasureMapper,
        selectors.getAllTroBooksData,
        selectors.getClassOrGroupOfficialTeacher,
    ],
    (
        isDataReady,
        licensingInfo,
        { studentsByGrade, results, year },
        selectedPeriod,
        gradeToMeasureMapper,
        allTroBooksData,
        officialTeacherInfo,
    ) =>
        getTroClassSummaryPdfData({
            isDataReady,
            selectedPeriod,
            licensingInfo,
            studentsByGrade,
            allTroBooksData,
            results,
            year,
            gradeToMeasureMapper,
            officialTeacherInfo,
        }),
);

selectors.getAvailablePrintItems = createSelector(
    [
        selectors.getSelectedPeriod,
        selectors.getLicensingInfo,
        selectors.getChosenClassStudentsByGrade,
        selectors.getGradeToMeasureMapper,
        selectors.getPdfAvailability,
    ],
    (selectedPeriod, licenseInfo, studentsByGrade, gradeToMeasureMapper, pdfAvailabilityConfig) =>
        getAvailablePrintItems(
            selectedPeriod,
            licenseInfo,
            studentsByGrade,
            gradeToMeasureMapper,
            pdfAvailabilityConfig,
        ),
);

selectors.getClassSummaryPdfData = createSelector(
    [
        selectors.isDataReady,
        selectors.getStudentsResultsCurrentYear,
        selectors.getInterps,
        selectors.getSelectedPeriod,
        selectors.getCurrentPeriod,
        selectors.getGradeToMeasureMapper,
        selectors.getSummerPeriodLabel,
    ],
    (
        isDataReady,
        { studentsByGrade, results, year },
        interps,
        selectedPeriod,
        currentPeriod,
        gradeToMeasureMapper,
        summerPeriodLabel,
    ) =>
        getClassSummaryPdfData({
            isDataReady,
            studentsByGrade,
            results,
            interps,
            year,
            selectedPeriod,
            currentPeriod,
            gradeToMeasureMapper,
            summerPeriodLabel,
        }),
);

selectors.isResetButtonAvailableInClass = createSelector(
    [
        selectors.getCurrentSchoolYear,
        selectors.getSelectedPeriod,
        selectors.getGradesInCurrentClass,
        selectors.getResults,
        selectors.getIsDTC,
    ],
    (currentSchoolYear, selectedPeriod, gradesInCurrentClass, results, isDTC) => {
        const isSecondAttemptCompositeExist =
            Object.values(results).reduce((acc, resultObject) => {
                return [
                    ...acc,
                    ...resultObject.statuses.filter(
                        status =>
                            status.measure === MEASURE.OVERALL &&
                            status.period === PERIOD.SUMMER &&
                            status.grade === GRADE.THIRD_GRADE &&
                            status.schoolYear === currentSchoolYear &&
                            status.score &&
                            status.attemptNumber === 2,
                    ),
                ];
            }, []).length > 0;
        return (
            isDTC &&
            selectedPeriod === PERIOD.SUMMER &&
            gradesInCurrentClass.includes(GRADE.THIRD_GRADE) &&
            isSecondAttemptCompositeExist
        );
    },
);
